import { noChange } from 'lit'
import type { ElementPart } from 'lit'
import { AsyncDirective, directive } from 'lit/async-directive.js'
import { PartType } from 'lit/directive.js'
import { Subject, fromEvent, takeUntil } from 'rxjs'
import { reducedMotion$ } from './reduced-motion'

export interface TapWaveOptions {
	/** Wash colour: 'add' → primary, 'remove' → neutral. A colour discriminator
	 *  only — no glyph is ever rendered. Default: 'add' */
	tone?: 'add' | 'remove'
	/**
	 * CSS selector for the ripple's HOST, resolved via `this.element.closest(boundary)`
	 * — use this when the tap TRIGGER is smaller than the visual container it
	 * should wash (e.g. one half of a split-stepper wrapper). Trigger and
	 * boundary must share a shadow root for `closest` to find it. When set and
	 * resolvable, the wave is sized from the boundary's rect, positioned
	 * against the click translated into the boundary's box, and appended into
	 * the boundary. When absent, or when `closest` finds nothing, the host is
	 * the trigger element itself — today's behaviour, unchanged.
	 *
	 * This directive never touches the host's `overflow` — clipping the wave
	 * to the boundary's shape is the caller's responsibility (give the
	 * boundary its own `overflow: hidden`).
	 */
	boundary?: string
}

const DEFAULT_TONE: TapWaveOptions['tone'] = 'add'

const WAVE_MS = 540
const PRIMARY = 'var(--schmancy-sys-color-primary-default)'
const NEUTRAL = 'var(--schmancy-sys-color-surface-onVariant)'

/**
 * Feedback for an element with no chrome at rest (a tap-to-add card/row).
 *
 * SHADOW-DOM SAFE — the crux. A consumer applies this inside its own shadow
 * root, where a `document.head` <style> never reaches, so a class-based
 * ripple renders as an unstyled, invisible span. This directive carries ALL
 * its styling INLINE and animates via the Web Animations API — nothing a
 * shadow boundary can strip. CSS custom properties still inherit across the
 * boundary, so the theme colour var resolves inline.
 *
 * `prefers-reduced-motion` replaces the wave with a brief flat tint.
 *
 * The `click` listener always binds to the TRIGGER (the element this
 * directive is applied to), so a small tap zone still registers every click.
 * The ripple's HOST — what it's sized from, positioned against, and painted
 * into — is the trigger itself, unless `boundary` names an ancestor selector
 * that resolves via `closest`, in which case the host is that ancestor (see
 * `TapWaveOptions.boundary`).
 */
class TapWaveDirective extends AsyncDirective {
	private element!: HTMLElement
	private boundary?: string
	private readonly teardown$ = new Subject<void>()
	private color = PRIMARY

	render(_options?: TapWaveOptions) {
		void _options
		return noChange
	}

	override update(part: ElementPart, [options]: [TapWaveOptions?]) {
		if (part.type !== PartType.ELEMENT) {
			throw new Error('tapWave directive must be used on an element')
		}
		this.element = part.element as HTMLElement
		this.boundary = options?.boundary
		this.color = (options?.tone ?? DEFAULT_TONE) === 'add' ? PRIMARY : NEUTRAL
		this.listen()
		return noChange
	}

	override reconnected() {
		this.listen()
	}

	override disconnected() {
		this.teardown$.next()
	}

	private listen() {
		this.teardown$.next()
		fromEvent<MouseEvent>(this.element, 'click')
			.pipe(takeUntil(this.teardown$))
			.subscribe(e => this.fire(e))
	}

	private fire(event: MouseEvent) {
		// An element the DOM marks aria-disabled="true" performs no mutation, so
		// it gets no feedback.
		if (this.element.getAttribute('aria-disabled') === 'true') return
		if (reducedMotion$.value) {
			this.tint()
			return
		}
		this.wave(event)
	}

	/** The ripple's host — the boundary ancestor when `boundary` resolves via
	 *  `closest`, else the trigger element itself. */
	private host(): HTMLElement {
		const found = this.boundary ? this.element.closest<HTMLElement>(this.boundary) : null
		return found ?? this.element
	}

	private wave(event: MouseEvent) {
		const el = this.host()
		if (getComputedStyle(el).position === 'static') el.style.position = 'relative'

		const rect = el.getBoundingClientRect()
		const x = event.clientX - rect.left
		const y = event.clientY - rect.top
		const corners: Array<[number, number]> = [
			[0, 0],
			[rect.width, 0],
			[0, rect.height],
			[rect.width, rect.height],
		]
		const radius = Math.max(...corners.map(([cx, cy]) => Math.hypot(cx - x, cy - y)))
		const d = radius * 2

		const wave = document.createElement('span')
		wave.setAttribute('aria-hidden', 'true')
		const s = wave.style
		s.position = 'absolute'
		s.left = `${x - radius}px`
		s.top = `${y - radius}px`
		s.width = `${d}px`
		s.height = `${d}px`
		s.borderRadius = '9999px'
		s.background = this.color
		s.filter = 'blur(6px)'
		s.pointerEvents = 'none'
		s.transform = 'scale(0)'
		s.opacity = '0'
		s.zIndex = '20'
		el.appendChild(wave)

		const anim = wave.animate(
			[
				{ transform: 'scale(0)', opacity: 0 },
				{ opacity: 0.55, offset: 0.22 },
				{ transform: 'scale(1)', opacity: 0 },
			],
			{ duration: WAVE_MS, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)' },
		)
		anim.finished.then(() => wave.remove()).catch(() => wave.remove())
	}

	private tint() {
		const el = this.host()
		const tint = document.createElement('span')
		tint.setAttribute('aria-hidden', 'true')
		const s = tint.style
		s.position = 'absolute'
		s.inset = '0'
		s.background = this.color
		s.opacity = '0'
		s.pointerEvents = 'none'
		el.appendChild(tint)
		const anim = tint.animate([{ opacity: 0 }, { opacity: 0.16, offset: 0.3 }, { opacity: 0 }], {
			duration: 340,
			easing: 'ease-out',
		})
		anim.finished.then(() => tint.remove()).catch(() => tint.remove())
	}
}

export const tapWave = directive(TapWaveDirective)
